import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router, NavigationEnd, NavigationCancel, NavigationError, NavigationStart } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from './multi-language/language.service';
import { filter } from 'rxjs/operators';
import { SpinnerService } from './Theme/shared/service/spinner.service';
import { Subscription } from 'rxjs';
import { ThemeService } from './Theme/shared/service/theme.service';
import { CpCompanyService } from './admin/tf-admin/tf-configuration/tf-company/service/cp-company.service';
import { NotificationMobileToastService } from './Theme/shared/components/notification-mobile-toast/notification-mobile-toast.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})

export class AppComponent {
  langset: any;
  uploadedLogo: File | null = null;
  @Input() navCollapsed: boolean;
  @Output() onNavCollapse = new EventEmitter();
  windowWidth: number;
  title = 'zum-connect-frontend';
  isLoading = false;
  private loadingSubscription: Subscription;
  private routerSubscription: Subscription;
  private favIconSubscription: Subscription;
  private themeApplied = false;
  private themeLoadInProgress = false;
  constructor(private router: Router,
    public translate: TranslateService,
    public langService: LanguageService,
    public spinnerService: SpinnerService,
    private themeService: ThemeService,
    private cpCompanyService: CpCompanyService,
    mobileTopBannerService: NotificationMobileToastService) {
    mobileTopBannerService.logViewportStatus('AppComponent constructor — service injected');
  }
  // life cycle event
  // ngOnInit() {
  //   this.router.events.pipe(
  //     filter(event => event instanceof NavigationEnd)
  //   ).subscribe((event: NavigationEnd) => {
  //     if (event.url === '/home') {
  //       localStorage.clear();
  //       this.setupLanguageSettings();
  //     }
  //     window.scrollTo(0, 0);
  //   });

  //   // Initialize language settings
  //   this.setupLanguageSettings();
  // }
  // ngOnInit() {
  //   // Listen to router events to manage loading state
  //   this.router.events.subscribe(event => {
  //     if (event instanceof NavigationStart) {
  //       this.isLoading = true;
  //     }
  //     else if (
  //       event instanceof NavigationEnd || 
  //       event instanceof NavigationCancel || 
  //       event instanceof NavigationError
  //     ) {
  //       // Stop loading after navigation completes or fails
  //       setTimeout(() => {
  //         this.isLoading = false;
  //       }, 500); // Small delay to ensure smooth transition
  //     }
  //   });

  //   // Your existing router event logic
  //   this.router.events.pipe(
  //     filter(event => event instanceof NavigationEnd)
  //   ).subscribe((event: NavigationEnd) => {
  //     if (event.url === '/home') {
  //       localStorage.clear();
  //       this.setupLanguageSettings();
  //     }
  //     window.scrollTo(0, 0);
  //   });

  //   // Initialize language settings
  //   this.setupLanguageSettings();
  // }

  ngOnInit() {
    // Subscribe to spinner service loading state
    this.loadingSubscription = this.spinnerService.loading$.subscribe(
      (loading) => {
        this.isLoading = loading;
      }
    );

    // Apply favicon app-wide (browser tab icon) when company favicon changes
    this.favIconSubscription = this.cpCompanyService.pictureUrlFavIcon$.subscribe(url => {
      this.applyFavIcon(url);
    });
    // Apply initial favicon if already loaded (e.g. by app initializer)
    const currentFavIcon = this.cpCompanyService.getCurrentFavIconUrl();
    if (currentFavIcon) {
      this.applyFavIcon(currentFavIcon);
    }

    // Listen to router events
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.spinnerService.show();
      }
      else if (
        event instanceof NavigationEnd || 
        event instanceof NavigationCancel || 
        event instanceof NavigationError
      ) {
        // Hide spinner after a short delay
        setTimeout(() => {
          this.spinnerService.hide();
        }, 500);
      }
    });

    // Existing navigation end logic
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (event.url === '/home') {
        localStorage.clear();
        this.setupLanguageSettings();
      }
      window.scrollTo(0, 0);
      this.ensureThemeLoaded();
    });

    // Initialize language settings
    this.setupLanguageSettings();

    // Ensure theme colors/buttons are applied immediately on first load
    this.ensureThemeLoaded();
  }

  ngOnDestroy() {
    // Prevent memory leaks by unsubscribing
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.favIconSubscription) {
      this.favIconSubscription.unsubscribe();
    }
  }

  /**
   * Update document <link rel="icon"> so favicon applies on all routes (like logo).
   */
  private applyFavIcon(url: string | null): void {
    if (typeof document === 'undefined' || !document.head) { return; }
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      document.head.appendChild(link);
    }
    if (url) {
      link.setAttribute('href', url);
      link.setAttribute('type', url.toLowerCase().includes('.png') ? 'image/png' : 'image/x-icon');
    }
  }
  // private setupLanguageSettings() {
  //   this.langset = this.translate.getBrowserLang();
  //   localStorage.setItem("lang", this.langset);
  //   if (!(this.langset in ["en", "De", "fr"])) {
  //     this.translate.setDefaultLang('en');
  //     this.translate.use('en');
  //   }
  // }
  private setupLanguageSettings() {
    // Check if user has explicitly selected a language
    const userSelectedLang = localStorage.getItem('langchanged');
    
    if (userSelectedLang && ['en', 'De', 'fr'].includes(userSelectedLang)) {
      // Use the saved language preference
      this.langset = userSelectedLang;
      this.translate.use(userSelectedLang);
    } else {
      // No saved preference, use browser language
      this.langset = this.translate.getBrowserLang();
      localStorage.setItem("lang", this.langset);
      
      if (!['en', 'De', 'fr'].includes(this.langset)) {
        // If browser language is not supported, default to English
        this.translate.setDefaultLang('en');
        this.translate.use('en');
        this.langset = 'en';
        localStorage.setItem("lang", 'en');
      } else {
        this.translate.use(this.langset);
      }
    }
    
    // Always ensure the lang setting exists for components that expect it
    if (!localStorage.getItem("lang")) {
      localStorage.setItem("lang", this.langset);
    }
  }

  private ensureThemeLoaded(): void {
    if (this.themeApplied || this.themeLoadInProgress) {
      return;
    }
    this.themeLoadInProgress = true;
    this.themeService.getCompleteTheme().subscribe({
      next: () => {
        console.log('AppComponent: Theme loaded/applied');
        this.themeApplied = true;
        this.themeLoadInProgress = false;
      },
      error: (err) => {
        console.error('❌ AppComponent: Theme load failed (will retry on next navigation)', err);
        this.themeLoadInProgress = false;
      }
    });
  }
}
